import { filters, getFilterBackend } from '@imagise/entities';
import { FilterTypes, SobelFilterTypes } from '../../domain/enums/editor.enum';
import {
  EditorBackendFilterArgs,
  EditorFilterArgs,
  EditorFilterCommittedEdit,
  EditorSobelFilterArgs,
} from '../../domain/editor-domain.models';
import { clamp, parseHexColor } from './canvas-color.utils';
import { cloneCanvas, createCanvas } from './canvas-dom.utils';

export type EditorFilterParams = EditorFilterCommittedEdit['params'];

const CARTOON_EDGE_THRESHOLD = 96;

type CanvasFilter = filters.BaseFilter<string, Record<string, unknown>, Record<string, unknown>>;

export function applyFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorFilterParams,
): HTMLCanvasElement {
  switch (params.filterType) {
    case FilterTypes.Exposure:
      return applyExposureFilter(document, sourceCanvas, params.filterArgs);
    case FilterTypes.Grayscale:
      return applyEntityFilters(document, sourceCanvas, [new filters.Grayscale()]);
    case FilterTypes.Inverse:
      return applyEntityFilters(document, sourceCanvas, [new filters.Invert()]);
    case FilterTypes.Binarize:
      return applyBinarizeFilter(document, sourceCanvas, params.filterArgs);
    case FilterTypes.Sharpen:
      return applySharpenFilter(document, sourceCanvas, params.filterArgs);
    case FilterTypes.Outline:
      return applyEntityFilters(document, sourceCanvas, [
        new filters.Grayscale(),
        new filters.Convolute({
          matrix: [-1, -1, -1, -1, 8, -1, -1, -1, -1],
          opaque: true,
        }),
      ]);
    case FilterTypes.MotionBlur:
      return applyEntityFilters(document, sourceCanvas, [
        new filters.Convolute({
          matrix: createMotionBlurKernel(),
          opaque: true,
        }),
      ]);
    case FilterTypes.Emboss:
      return applyEntityFilters(document, sourceCanvas, [
        new filters.Convolute({
          matrix: [-1, -1, 0, -1, 0, 1, 0, 1, 1],
          opaque: true,
        }),
        new filters.Grayscale(),
      ]);
    case FilterTypes.Sobel:
      return applySobelFilter(document, sourceCanvas, params.filterArgs);
    case FilterTypes.GaussianBlur:
      return applyGaussianBlurFilter(document, sourceCanvas, params.filterArgs);
    case FilterTypes.ColorBurn:
      return applyColorBurnFilter(document, sourceCanvas);
    case FilterTypes.Cartoon:
      return applyCartoonFilter(document, sourceCanvas);
    case FilterTypes.BigRectangular:
    case FilterTypes.SmallRectangular:
    case FilterTypes.Median:
    case FilterTypes.GaussWiener:
    case FilterTypes.MotionWiener:
    case FilterTypes.BilateralSmoothing:
      return applySupportedBackendFilter(document, sourceCanvas, params.filterType, params.filterArgs);
    default:
      return cloneCanvas(document, sourceCanvas);
  }
}

function applyExposureFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  if (args?.kind !== 'exposure') {
    return cloneCanvas(document, sourceCanvas);
  }
  const exposureArgs = args;

  const canvas = applyEntityFilters(document, sourceCanvas, [
    new filters.Brightness({ brightness: exposureArgs.brightness / 100 }),
    new filters.Contrast({ contrast: exposureArgs.contrast / 100 }),
    new filters.Gamma({
      gamma: [
        resolveGammaValue(exposureArgs.gammaRed),
        resolveGammaValue(exposureArgs.gammaGreen),
        resolveGammaValue(exposureArgs.gammaBlue),
      ],
    }),
  ]);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return sourceCanvas;
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const contrastScale = Math.exp(exposureArgs.contrast / 62);
  const brightnessOffset = exposureArgs.brightness / 100;
  const amplitude = contrastScale;
  const offset = brightnessOffset + (1 - contrastScale) / 2;

  const gammaRed = Math.exp(-exposureArgs.gammaRed / 62);
  const gammaGreen = Math.exp(-exposureArgs.gammaGreen / 62);
  const gammaBlue = Math.exp(-exposureArgs.gammaBlue / 62);

  for (let index = 0; index < data.length; index += 4) {
    data[index] = applyGammaCurve(data[index], amplitude, offset, gammaRed);
    data[index + 1] = applyGammaCurve(data[index + 1], amplitude, offset, gammaGreen);
    data[index + 2] = applyGammaCurve(data[index + 2], amplitude, offset, gammaBlue);
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function applyBinarizeFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  if (args?.kind !== 'binarization') {
    return cloneCanvas(document, sourceCanvas);
  }
  const binarizeArgs = args;

  const canvas = cloneCanvas(document, sourceCanvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return sourceCanvas;
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const threshold = clamp(binarizeArgs.threshold, 0, 255);
  const lowColor = parseHexColor(binarizeArgs.lowColor, [0, 0, 0, 255]);
  const highColor = parseHexColor(binarizeArgs.highColor, [255, 255, 255, 255]);

  for (let index = 0; index < data.length; index += 4) {
    const grayscale = (data[index] + data[index + 1] + data[index + 2]) / 3;
    const target = grayscale < threshold ? lowColor : highColor;

    data[index] = target[0];
    data[index + 1] = target[1];
    data[index + 2] = target[2];
    data[index + 3] = target[3];
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function applySobelFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  if (args?.kind !== 'sobel') {
    return cloneCanvas(document, sourceCanvas);
  }

  return applyEntityFilters(document, sourceCanvas, [
    new filters.Convolute({ matrix: getSobelKernel(args), opaque: true }),
  ]);
}

function applyGaussianBlurFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  const backendArgs = tryGetBackendFilterArgs(args);
  if (backendArgs) {
    return applyEntityFilters(document, sourceCanvas, [
      new filters.Blur({
        blur: toBlurStrength(Math.max(backendArgs.size ?? 1, backendArgs.sigma ?? 1)),
      }),
    ]);
  }

  if (args?.kind !== 'gaussian-blur') {
    return cloneCanvas(document, sourceCanvas);
  }

  return applyEntityFilters(document, sourceCanvas, [
    new filters.Blur({ blur: toBlurStrength(args.stdDeviation) }),
  ]);
}

function applySharpenFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  const backendArgs = tryGetBackendFilterArgs(args);
  const centerWeight = backendArgs ? clamp(5 + (backendArgs.sigma ?? 1) / 2, 5, 9) : 5;

  return applyEntityFilters(document, sourceCanvas, [
    new filters.Convolute({
      matrix: [0, -1, 0, -1, centerWeight, -1, 0, -1, 0],
      opaque: true,
    }),
  ]);
}

function applySupportedBackendFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  filterType: FilterTypes,
  args: EditorFilterArgs | null,
): HTMLCanvasElement {
  const backendArgs = tryGetBackendFilterArgs(args);

  switch (filterType) {
    case FilterTypes.BigRectangular:
      return applyAverageKernelFilter(document, sourceCanvas, 7);
    case FilterTypes.SmallRectangular:
      return applyAverageKernelFilter(document, sourceCanvas, 3);
    case FilterTypes.Median:
    case FilterTypes.BilateralSmoothing:
      return applyAverageKernelFilter(document, sourceCanvas, normalizeKernelSize(backendArgs?.size ?? 3));
    case FilterTypes.GaussWiener:
      return applyEntityFilters(document, sourceCanvas, [
        new filters.Blur({
          blur: toBlurStrength(Math.max(backendArgs?.size ?? 1, backendArgs?.sigma ?? 1)),
        }),
        new filters.Convolute({ matrix: [0, -1, 0, -1, 5, -1, 0, -1, 0], opaque: true }),
      ]);
    case FilterTypes.MotionWiener:
      return applyEntityFilters(document, sourceCanvas, [
        new filters.Convolute({
          matrix: createMotionBlurKernel(normalizeKernelSize(backendArgs?.size ?? 5), backendArgs?.angle ?? 0),
          opaque: true,
        }),
      ]);
    default:
      return cloneCanvas(document, sourceCanvas);
  }
}

function applyColorBurnFilter(document: Document, sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
  const outlineCanvas = applyEntityFilters(document, sourceCanvas, [
    new filters.Convolute({
      matrix: [-1, -1, -1, -1, 9, -2, -1, -1, -1],
      opaque: true,
    }),
  ]);

  const outputCanvas = cloneCanvas(document, sourceCanvas);
  const outputContext = outputCanvas.getContext('2d');
  if (!outputContext) {
    return sourceCanvas;
  }

  outputContext.drawImage(sourceCanvas, 0, 0);
  outputContext.globalCompositeOperation = 'color-burn';
  outputContext.drawImage(outlineCanvas, 0, 0);
  outputContext.globalCompositeOperation = 'source-over';

  return outputCanvas;
}

function applyCartoonFilter(document: Document, sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
  const outlineCanvas = applyEntityFilters(document, sourceCanvas, [
    new filters.Grayscale(),
    new filters.Blur({ blur: toBlurStrength(1) }),
    new filters.Convolute({
      matrix: [-1, -1, -1, -1, 8, -1, -1, -1, -1],
      opaque: true,
    }),
  ]);

  const outlineContext = outlineCanvas.getContext('2d');
  if (!outlineContext) {
    return sourceCanvas;
  }

  const outlineData = outlineContext.getImageData(0, 0, outlineCanvas.width, outlineCanvas.height);

  for (let index = 0; index < outlineData.data.length; index += 4) {
    const intensity = outlineData.data[index];
    if (intensity > CARTOON_EDGE_THRESHOLD) {
      outlineData.data[index + 3] = 0;
    } else {
      outlineData.data[index] = 0;
      outlineData.data[index + 1] = 0;
      outlineData.data[index + 2] = 0;
      outlineData.data[index + 3] = 255;
    }
  }

  outlineContext.putImageData(outlineData, 0, 0);

  const backgroundCanvas = applyEntityFilters(document, sourceCanvas, [
    new filters.Blur({ blur: toBlurStrength(3) }),
  ]);

  const blendedCanvas = cloneCanvas(document, backgroundCanvas);
  const blendedContext = blendedCanvas.getContext('2d');
  if (!blendedContext) {
    return sourceCanvas;
  }

  blendedContext.globalCompositeOperation = 'multiply';
  blendedContext.drawImage(outlineCanvas, 0, 0);
  blendedContext.globalCompositeOperation = 'source-over';

  return applyEntityFilters(document, blendedCanvas, [
    new filters.Blur({ blur: toBlurStrength(1) }),
    new filters.Convolute({ matrix: [0, -1, 0, -1, 5, -1, 0, -1, 0], opaque: true }),
  ]);
}

function applyEntityFilters(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  filtersList: CanvasFilter[],
): HTMLCanvasElement {
  const target = createCanvas(document, sourceCanvas.width, sourceCanvas.height);

  // Not cached: getFilterBackend itself recreates the backend when the WebGL
  // context is lost, while a local cache would keep a dead reference.
  getFilterBackend().applyFilters(filtersList, sourceCanvas, sourceCanvas.width, sourceCanvas.height, target);

  return target;
}

function applyAverageKernelFilter(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  size: number,
): HTMLCanvasElement {
  const normalizedSize = normalizeKernelSize(size);
  const value = 1 / (normalizedSize * normalizedSize);

  return applyEntityFilters(document, sourceCanvas, [
    new filters.Convolute({
      matrix: Array(normalizedSize * normalizedSize).fill(value),
      opaque: true,
    }),
  ]);
}

function tryGetBackendFilterArgs(args: EditorFilterArgs | null): EditorBackendFilterArgs | null {
  return args?.kind === 'backend-filter' ? args : null;
}

export function createMotionBlurKernel(size = 9, angle = 45): number[] {
  const kernel = Array(size * size).fill(0);
  const center = (size - 1) / 2;
  const radians = (angle * Math.PI) / 180;
  const points = new Set<number>();

  for (let step = -center; step <= center; step += 1) {
    const x = Math.round(center + step * Math.cos(radians));
    const y = Math.round(center + step * Math.sin(radians));
    const clampedX = clamp(x, 0, size - 1);
    const clampedY = clamp(y, 0, size - 1);
    points.add(clampedY * size + clampedX);
  }

  const weight = points.size > 0 ? 1 / points.size : 1;
  for (const pointIndex of points) {
    kernel[pointIndex] = weight;
  }

  return kernel;
}

export function getSobelKernel(args: EditorSobelFilterArgs): number[] {
  switch (args.sobelFilterType) {
    case SobelFilterTypes.Top:
      return [1, 2, 1, 0, 0, 0, -1, -2, -1];
    case SobelFilterTypes.Bottom:
      return [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    case SobelFilterTypes.Left:
      return [1, 0, -1, 2, 0, -2, 1, 0, -1];
    case SobelFilterTypes.Right:
      return [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    default:
      return [1, 2, 1, 0, 0, 0, -1, -2, -1];
  }
}

export function normalizeKernelSize(value: number): number {
  const rounded = Math.max(1, Math.round(value));
  if (rounded <= 3) {
    return 3;
  }
  if (rounded <= 5) {
    return 5;
  }
  if (rounded <= 7) {
    return 7;
  }

  return 9;
}

export function toBlurStrength(stdDeviation: number): number {
  return clamp(stdDeviation / 25, 0, 1);
}

export function applyGammaCurve(channel: number, amplitude: number, offset: number, exponent: number): number {
  const normalized = channel / 255;
  const transformed = (amplitude * Math.pow(normalized, exponent) + offset) * 255;
  return clamp(Math.round(transformed), 0, 255);
}

export function resolveGammaValue(gammaInput: number): number {
  const gamma = Math.exp(-gammaInput / 62);
  return clamp(gamma, 0.01, 2.2);
}
